import React from 'react';

/**
 * Page header: `Effort  [ Me ▾ ]  [ Last 14 days ▾ ]  [ Log time ]`.
 *
 * The person switcher renders straight from `members` (fn_effort_members) with no
 * capability branching: the backend returns only the caller without
 * `effort.read.all`, so the list is authoritative — one option means "just you".
 * Self is the first entry (`is_me`) and maps to the empty value so selecting it
 * clears `?member` and reads the own timesheet.
 *
 * @param {{id:string,name:string,is_me?:boolean}[]} props.members  Switcher options.
 * @param {string|null} props.selectedMemberId  null = self ("Me").
 * @param {(id:string|null)=>void} props.onSelectMember
 * @param {number}   props.rangeDays
 * @param {(days:number)=>void} props.onSelectRange
 * @param {()=>void} props.onLog          Opens the log-after-the-fact dialog.
 */
const RANGE_OPTIONS = [
  { days: 7, label: 'Last 7 days' },
  { days: 14, label: 'Last 14 days' },
  { days: 30, label: 'Last 30 days' },
];

export default function EffortHeader({
  members = [],
  selectedMemberId = null,
  onSelectMember,
  rangeDays = 14,
  onSelectRange,
  onLog,
}) {
  const showSwitcher = members.length > 0;

  return (
    <div className="flex flex-col gap-5">
      {/* Title + subtitle take the full width (their own row) so the subtitle
          never wraps against the controls; the controls sit on a left-aligned
          row below. */}
      <div className="w-full min-w-0">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Effort</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Time and effort logged against clients — yours, and your agents'.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {showSwitcher && (
          <select
            aria-label="Whose timesheet"
            data-testid="member-switcher"
            value={selectedMemberId ?? ''}
            onChange={(e) => onSelectMember?.(e.target.value || null)}
            className="text-sm rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-1.5"
          >
            {members.map((m) => (
              <option key={m.id} value={m.is_me ? '' : m.id}>
                {m.is_me ? 'Me' : m.name}
              </option>
            ))}
          </select>
        )}

        <select
          aria-label="Date range"
          data-testid="range-selector"
          value={rangeDays}
          onChange={(e) => onSelectRange?.(Number(e.target.value))}
          className="text-sm rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-1.5"
        >
          {RANGE_OPTIONS.map((o) => (
            <option key={o.days} value={o.days}>
              {o.label}
            </option>
          ))}
        </select>

        <button
          type="button"
          onClick={() => onLog?.()}
          className="text-sm font-medium rounded-lg bg-[#32B1FF] hover:bg-[#1a9ce6] text-white px-3 py-1.5"
        >
          Log time
        </button>
      </div>
    </div>
  );
}
